import { useState, useCallback, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  Alert,
  AlertTitle,
  Paper,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import { Upload, CheckCircle, Refresh } from "@mui/icons-material";
import { PlatformPage } from "@tracabois/template";
import {
  useCsvImportConfirmMutation,
  useCsvImportCancelMutation,
  useLazyCsvImportStatusQuery,
  CsvImportConfirmResult,
  CsvImportRow,
  CompanyInfo,
  DuplicateAction,
  DuplicateActionType,
  ImportPreviewTable,
  ImportStats,
  ImportResultsTable,
} from "@tracabois/domain";
import { useNavigate, useParams } from "react-router-dom";

type PreviewStep = "loading" | "preview" | "importing" | "results";

// Helper to get name from either string or object with name property (for Kotlin enums)
const getEnumName = (value: any): string => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && value.name) return value.name;
  return String(value);
};

export const CsvImportPreviewPage = () => {
  const { importId } = useParams<{ importId: string }>();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState<PreviewStep>("loading");
  const [confirmResult, setConfirmResult] = useState<CsvImportConfirmResult | null>(null);
  const [duplicateActions, setDuplicateActions] = useState<Map<number, DuplicateActionType>>(
    new Map()
  );
  const [defaultDuplicateAction, setDefaultDuplicateAction] = useState<DuplicateActionType>(
    DuplicateAction.SKIP
  );
  const [editedValues, setEditedValues] = useState<Map<number, Partial<CsvImportRow>>>(new Map());
  const [excludedRows, setExcludedRows] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const [confirmImport, { isLoading: isConfirming }] = useCsvImportConfirmMutation();
  const [cancelImport] = useCsvImportCancelMutation();
  const [getStatus, { data: statusData }] = useLazyCsvImportStatusQuery();

  // Load import data on mount
  useEffect(() => {
    if (!importId) return;

    const loadData = async () => {
      try {
        const result = await getStatus({ importId } as any).unwrap();
        const statusName = getEnumName(result.status);

        if (statusName === "COMPLETED" || statusName === "IMPORT_ERROR") {
          setConfirmResult({
            importId,
            status: result.status,
            stats: result.stats,
            results: [],
          } as any);
          setCurrentStep("results");
        } else if (statusName === "IMPORTING" || statusName === "CONFIRMED") {
          setCurrentStep("importing");
        } else {
          // PREVIEW or similar — initialize duplicate actions
          const rows = (result.preview as CsvImportRow[]) || [];
          const actions = new Map<number, DuplicateActionType>();
          rows.forEach((row) => {
            if (getEnumName(row.status) === "DUPLICATE") {
              actions.set(row.rowNumber, DuplicateAction.SKIP);
            }
          });
          setDuplicateActions(actions);
          setCurrentStep("preview");
        }
      } catch {
        setError("Impossible de charger les données de l'import");
        setCurrentStep("preview");
      }
    };

    loadData();
  }, [importId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for status during import
  useEffect(() => {
    if (currentStep !== "importing" || !importId) return;

    const pollInterval = setInterval(async () => {
      const result = await getStatus({ importId } as any);
      const status = result.data?.status;
      const statusName = getEnumName(status);

      if (statusName === "COMPLETED" || statusName === "IMPORT_ERROR") {
        clearInterval(pollInterval);
        setCurrentStep("results");
        if (result.data) {
          setConfirmResult({
            importId,
            status: result.data.status,
            stats: result.data.stats,
            results: [],
          } as any);
        }
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [currentStep, importId, getStatus]);

  const handleDuplicateActionChange = useCallback(
    (rowNumber: number, action: DuplicateActionType) => {
      setDuplicateActions((prev) => {
        const next = new Map(prev);
        next.set(rowNumber, action);
        return next;
      });
    },
    []
  );

  const handleBulkDuplicateActionChange = useCallback(
    (action: DuplicateActionType) => {
      setDefaultDuplicateAction(action);
      setDuplicateActions((prev) => {
        const next = new Map(prev);
        prev.forEach((_, rowNumber) => {
          next.set(rowNumber, action);
        });
        return next;
      });
    },
    []
  );

  const handleCellEdit = useCallback((rowNumber: number, field: keyof CsvImportRow, value: any) => {
    setEditedValues((prev) => {
      const next = new Map(prev);
      const existing = next.get(rowNumber) || {};
      next.set(rowNumber, { ...existing, [field]: value });
      return next;
    });
  }, []);

  const handleToggleRowExcluded = useCallback((rowNumber: number) => {
    setExcludedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowNumber)) {
        next.delete(rowNumber);
      } else {
        next.add(rowNumber);
      }
      return next;
    });
  }, []);

  const handleConfirmImport = useCallback(async () => {
    if (!importId) return;
    setError(null);

    try {
      const actionsObj: Record<string, string> = {};
      duplicateActions.forEach((action, rowNum) => {
        actionsObj[rowNum.toString()] = getEnumName(action);
      });

      const companyListToString = (list: CompanyInfo[] | undefined): string | undefined => {
        if (!list) return undefined;
        return list.map(c => c.name).join(" / ");
      };

      const editedRowsArray = Array.from(editedValues.entries()).map(([rowNumber, edits]) => ({
        rowNumber,
        name: edits.name ?? undefined,
        commune: edits.commune ?? undefined,
        codeInsee: edits.codeInsee ?? undefined,
        codePostal: edits.codePostal ?? undefined,
        latitude: edits.latitude ?? undefined,
        longitude: edits.longitude ?? undefined,
        buildingType: edits.buildingType ? getEnumName(edits.buildingType) : undefined,
        ownershipType: edits.ownershipType ? getEnumName(edits.ownershipType) : undefined,
        deliveryYear: edits.deliveryYear ?? undefined,
        surface: edits.surface ?? undefined,
        volume: edits.volume ?? undefined,
        externalId: edits.externalId ?? undefined,
        projectOwner: companyListToString(edits.projectOwner as CompanyInfo[] | undefined),
        architect: companyListToString(edits.architect as CompanyInfo[] | undefined),
        woodConstructor: companyListToString(edits.woodConstructor as CompanyInfo[] | undefined),
        lamelliste: companyListToString(edits.lamelliste as CompanyInfo[] | undefined),
        interiorCarpenter: companyListToString(edits.interiorCarpenter as CompanyInfo[] | undefined),
        supplier: companyListToString(edits.supplier as CompanyInfo[] | undefined),
      }));

      setCurrentStep("importing");

      const excludedRowsArray = Array.from(excludedRows);

      const result = await confirmImport({
        importId,
        duplicateActions: actionsObj as any,
        defaultDuplicateAction: getEnumName(defaultDuplicateAction) as any,
        editedRows: editedRowsArray.length > 0 ? editedRowsArray : undefined,
        excludedRows: excludedRowsArray.length > 0 ? excludedRowsArray : undefined,
      } as any).unwrap();

      setConfirmResult(result);

      const resultStatusName = getEnumName(result.status);
      if (resultStatusName === "COMPLETED" || resultStatusName === "IMPORT_ERROR") {
        setCurrentStep("results");
      }
    } catch (err: any) {
      setError(err?.data?.message || "Erreur lors de l'import");
      setCurrentStep("preview");
    }
  }, [importId, duplicateActions, defaultDuplicateAction, editedValues, excludedRows, confirmImport]);

  const handleCancel = useCallback(async () => {
    if (importId) {
      try {
        await cancelImport({ importId }).unwrap();
      } catch {
        // Ignore cancel errors
      }
    }
    navigate("/csv/import");
  }, [importId, cancelImport, navigate]);

  const handleReset = useCallback(() => {
    navigate("/csv/import");
  }, [navigate]);

  const previewRows = useMemo(() => {
    return (statusData?.preview as CsvImportRow[]) || [];
  }, [statusData?.preview]);

  const resultRows = useMemo(() => {
    return (confirmResult?.results as any[]) || [];
  }, [confirmResult?.results]);

  const canConfirm = useMemo(() => {
    if (!statusData?.stats) return false;
    const stats = statusData.stats;
    return (stats.validRows || 0) > 0 || (stats.duplicateRows || 0) > 0;
  }, [statusData?.stats]);

  const pageActions = useMemo(() => {
    if (currentStep === "preview") {
      return [
        {
          key: "cancel",
          label: "Annuler",
          variant: "outlined" as const,
          onClick: handleCancel,
        },
        {
          key: "confirm",
          label: isConfirming ? "Import en cours..." : "Confirmer l'import",
          variant: "contained" as const,
          onClick: handleConfirmImport,
          disabled: !canConfirm || isConfirming,
          startIcon: isConfirming ? <CircularProgress size={16} /> : <Upload />,
        },
      ];
    }
    if (currentStep === "results") {
      return [
        {
          key: "reset",
          label: "Nouvel import",
          variant: "contained" as const,
          onClick: handleReset,
          startIcon: <Refresh />,
        },
      ];
    }
    return undefined;
  }, [currentStep, handleCancel, handleConfirmImport, handleReset, canConfirm, isConfirming]);

  return (
    <PlatformPage title="Import CSV - Bâtiments Bois des Alpes" actions={pageActions}>
      <Stack spacing={3}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            <AlertTitle>Erreur</AlertTitle>
            {error}
          </Alert>
        )}

        {currentStep === "loading" && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {currentStep === "preview" && statusData && (
          <Stack spacing={3}>
            <ImportStats stats={statusData.stats} />

            {statusData.errors && (statusData.errors as any[]).length > 0 && (
              <Alert severity="warning">
                <AlertTitle>Erreurs globales</AlertTitle>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {(statusData.errors as any[]).slice(0, 5).map((err: any, idx: number) => (
                    <li key={idx}>{err.message}</li>
                  ))}
                </ul>
              </Alert>
            )}

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Aperçu des données ({previewRows.length} lignes)
              </Typography>
              <ImportPreviewTable
                rows={previewRows}
                duplicateActions={duplicateActions}
                onDuplicateActionChange={handleDuplicateActionChange}
                onBulkDuplicateActionChange={handleBulkDuplicateActionChange}
                editedValues={editedValues}
                onCellEdit={handleCellEdit}
                excludedRows={excludedRows}
                onToggleRowExcluded={handleToggleRowExcluded}
              />
            </Box>
          </Stack>
        )}

        {currentStep === "importing" && (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Import en cours...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Veuillez patienter, l'import peut prendre quelques minutes.
            </Typography>
            <LinearProgress sx={{ maxWidth: 400, mx: "auto" }} />
            {statusData?.stats && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  {(statusData.stats.created || 0) + (statusData.stats.updated || 0)} /{" "}
                  {statusData.stats.totalRows || 0} traités
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {currentStep === "results" && confirmResult && (
          <Stack spacing={3}>
            {getEnumName(confirmResult.status) === "COMPLETED" ? (
              <Alert severity="success" icon={<CheckCircle />}>
                <AlertTitle>Import terminé avec succès</AlertTitle>
                L'import a été effectué. Consultez les résultats ci-dessous.
              </Alert>
            ) : (
              <Alert severity="error">
                <AlertTitle>Import terminé avec des erreurs</AlertTitle>
                Certaines lignes n'ont pas pu être importées.
              </Alert>
            )}

            <ImportStats stats={confirmResult.stats} showResults />

            {resultRows.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Détail des résultats
                </Typography>
                <ImportResultsTable results={resultRows} />
              </Box>
            )}
          </Stack>
        )}
      </Stack>
    </PlatformPage>
  );
};
